import { BellOutlined, ExportOutlined } from "@ant-design/icons";
import { Button, Flex, Space, Spin, Typography } from "antd";
import { lazy, Suspense, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import homeImage1000 from "@/assets/images/home/homeImage-1000w.webp";
import homeImage1200 from "@/assets/images/home/homeImage-1200w.webp";
import homeImage1600 from "@/assets/images/home/homeImage-1600w.webp";
import homeImage400 from "@/assets/images/home/homeImage-400w.webp";
import homeImage600 from "@/assets/images/home/homeImage-600w.webp";
import homeImage800 from "@/assets/images/home/homeImage-800w.webp";
import ScienceMyHealthLogo from "@/assets/images/logos/sciencemyhealth/logo.webp";
import LinkButton from "@/components/LinkButton";
import SignupPopup from "@/components/SignUpModal";
import SponsorCard from "@/components/SponsorData/SponsorCard";
import SponsorSearchForStudy from "@/components/SponsorSearchForStudy";
import { useUserRegistryFeatureFlag } from "@/utils/featureFlags";
import { scrollElementIntoView } from "@/utils/iframeUtils";
import ErrorHandler from "@components/ErrorHandler";
import "./Home.css";

const SearchForm = lazy(() => import("../../../components/SearchForm"));

function HomeHeader(props: { scrollToTrialFinder: () => void }) {
    const { scrollToTrialFinder } = props;
    const { isEnabled: isUserRegistryEnabled } = useUserRegistryFeatureFlag();

    return (
        <Flex wrap="wrap" className="home-flex-wrapper">
            <img
                srcSet={`${homeImage400} 400w, ${homeImage600} 600w, ${homeImage800} 800w, ${homeImage1000} 1000w, ${homeImage1200} 1200w, ${homeImage1600} 1600w`}
                sizes="(max-width: 768px) 100vw, 40vw"
                src={homeImage800}
                alt="Group of people smiling to camera"
                fetchPriority="high"
                className="home-hero-image"
            />
            <div className="home-content">
                <div className="home-tab-content">
                    <Typography.Title level={1} className="homepage-header">
                        Find Your Clinical Trial
                    </Typography.Title>
                    <div className="home-tab-content-body">
                        <p>
                            Harness the Power of AI to Find thousands of available clinical trials. Find, Read, Listen
                            and get Matched with ease!
                        </p>
                        <Flex gap={24} align="center" vertical>
                            {isUserRegistryEnabled && <SignupPopup />}
                            <Space size="large" align="end">
                                <Button
                                    type="primary"
                                    size="large"
                                    className="take-me-to-trial-finder"
                                    onClick={scrollToTrialFinder}
                                >
                                    Find Studies Myself
                                </Button>
                                <span>or</span>
                                <LinkButton
                                    size="large"
                                    className="take-me-to-trial-finder"
                                    icon={<BellOutlined />}
                                    to="/new_alert"
                                    type="primary"
                                >
                                    Find Studies For Me
                                </LinkButton>
                            </Space>
                        </Flex>
                    </div>
                </div>
            </div>
        </Flex>
    );
}

function Home() {
    const location = useLocation();
    const trialFinder = useRef<HTMLElement>(null);

    const scrollToTrialFinder = () => {
        scrollElementIntoView(trialFinder.current);
    };

    useEffect(() => {
        // Timeout adds aesthetic delay, and fixes issue where navigating in from another page won't scroll
        const timeout = setTimeout(() => {
            // React router v6 will not scroll to hashlinks.
            // Listen to changes to the URL to determine when to scroll
            const removeHash = location.hash.replace("#", "");
            if (removeHash === "trialFinder") {
                scrollToTrialFinder();
            }
        }, 250);
        return () => clearTimeout(timeout);
    }, [location, trialFinder]);

    return (
        <div>
            <HomeHeader scrollToTrialFinder={scrollToTrialFinder} />
            <section id="scienceMyHealth">
                <div className="product-promo">
                    <div className="promo-banner">
                        <div style={{ paddingTop: "1rem", paddingBottom: "1rem" }}>
                            <a target="_blank" href="https://sciencemyhealth.com" rel="noreferrer">
                                <p
                                    style={{
                                        fontSize: "24px",
                                        margin: "0",
                                        whiteSpace: "normal",
                                        height: "auto",
                                        minHeight: "40px",
                                        lineHeight: "1.2",
                                        color: "black",
                                    }}
                                >
                                    Get the latest health and research science news. Visit{" "}
                                    <img
                                        decoding="async"
                                        style={{ height: "100%" }}
                                        height={64}
                                        src={ScienceMyHealthLogo}
                                        alt="logo of ScienceMyHealth"
                                    />
                                    <sup>
                                        <ExportOutlined style={{ fontSize: "80%" }} />
                                    </sup>
                                </p>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
            <section ref={trialFinder} id="trialFinder">
                <h1 className="inverse-header">Looking for a New Study?</h1>
                <Suspense fallback={<Spin />}>
                    <Flex>
                        <div className="content-body">
                            <ErrorHandler>
                                <SearchForm />
                            </ErrorHandler>
                        </div>
                        <SponsorCard />
                    </Flex>
                </Suspense>
            </section>
            <SponsorSearchForStudy />
        </div>
    );
}

export default Home;
